
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { BusinessOperations } from './BusinessOperations';
import { RiskAssessment } from '../workers-comp/RiskAssessment';
import { EmploymentPractices } from '../workers-comp/EmploymentPractices';
import { CarrierInfo } from '../workers-comp/CarrierInfo';
import type { Application } from '@/types/database.types';

interface CombinedOperationsAndRiskProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => Promise<void>;
}

export const CombinedOperationsAndRisk = ({ data, onSave }: CombinedOperationsAndRiskProps) => {
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, setValue, watch } = useForm<Partial<Application>>({
    defaultValues: {
      // Carrier info fields - moved to the top
      current_workers_comp_carrier: data.current_workers_comp_carrier || '',
      workers_comp_renewal_date: data.workers_comp_renewal_date || '',
      wc_coverage_cancelled: data.wc_coverage_cancelled || false,
      wc_cancellation_reason: data.wc_cancellation_reason || '',
      company_services_description: data.company_services_description || '',
      physical_operations_description: data.physical_operations_description || '',
      has_physical_stock: data.has_physical_stock || false,
      provides_engineering_services: data.provides_engineering_services || false,
      has_usl_jones_exposure: data.has_usl_jones_exposure || false,
      
      // Risk assessment fields
      has_over_road_trucking: data.has_over_road_trucking || false,
      has_cannabis_hemp_contact: data.has_cannabis_hemp_contact || false,
      cannabis_hemp_details: data.cannabis_hemp_details || '',
      has_elevated_workplace_risk: data.has_elevated_workplace_risk || false,
      workplace_risk_details: data.workplace_risk_details || '',
      has_company_aircraft: data.has_company_aircraft || false,
      has_marine_operations: data.has_marine_operations || false,
      has_heavy_machinery: data.has_heavy_machinery || false,
      has_recent_wc_claims: data.has_recent_wc_claims || false,
      
      // Employment practices fields
      has_recent_layoffs: data.has_recent_layoffs || false,
      recent_layoffs_details: data.recent_layoffs_details || '',
      layoff_count: data.layoff_count || 0,
      layoff_date: data.layoff_date || '',
      has_planned_layoffs: data.has_planned_layoffs || false,
      planned_layoffs_details: data.planned_layoffs_details || '',
      planned_layoff_count: data.planned_layoff_count || 0,
      planned_layoff_date: data.planned_layoff_date || '',
      voluntary_terminations: data.voluntary_terminations || 0,
      involuntary_terminations: data.involuntary_terminations || 0,
      has_foreign_travel: data.has_foreign_travel || false,
      foreign_travel_details: data.foreign_travel_details || '',
      foreign_travel_countries: data.foreign_travel_countries || '',
      foreign_travel_frequency: data.foreign_travel_frequency || '',
      has_epli_insurance: data.has_epli_insurance || false,
      epli_insurance_details: data.epli_insurance_details || '',
      
      // EPLI Survey specific fields - removed standalone_epli_policy field
      layoff_percentage: data.layoff_percentage || 0,
      layoff_releases_percentage: data.layoff_releases_percentage || 0,
    },
  });

  useEffect(() => {
    reset(data);
  }, [data, reset]);

  const onFormChange = handleSubmit(async (formData) => {
    try {
      await onSave(formData);
    } catch (error) {
      console.error("Error saving form data:", error);
      toast({
        title: "Save failed",
        description: "There was an error saving your progress",
        variant: "destructive",
      });
    }
  });

  return (
    <form onChange={onFormChange} onSubmit={(e) => e.preventDefault()}>
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6 space-y-6">
            <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
              <h2 className="text-2xl font-semibold">Operations & Risk Assessment</h2>
            </div>
            
            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
              <p className="text-muted-foreground text-sm">
                Detailed operational information enables precise risk assessment and carrier matching—ensuring PEO recommendations align with the organization's specific industry requirements and compliance needs.
              </p>
            </div>

            <BusinessOperations 
              register={register} 
              setValue={setValue} 
              watch={watch}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <CarrierInfo 
              register={register} 
              setValue={setValue}
              watch={watch}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <EmploymentPractices 
              register={register} 
              setValue={setValue} 
              watch={watch}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <RiskAssessment 
              register={register} 
              setValue={setValue} 
              watch={watch}
            />
          </CardContent>
        </Card>
      </div>
    </form>
  );
};
